/**
 * Hook para buscar carga de trabalho por profissional
 * 
 * Utiliza React Query para cache e gerenciamento de estado assíncrono.
 * Retorna dados formatados para gráfico de barras.
 * 
 * @module hooks/data/useWorkloadByProfessional
 */

import { useQuery } from '@tanstack/react-query';
import { getWorkloadByProfessionalApi } from '../../api/api';
import { logHttpError } from '../../utils/errorLogger';
import { getErrorMessage } from '../../utils/errorMessages';

// Query key para cache do React Query
export const WORKLOAD_BY_PROFESSIONAL_QUERY_KEY = ['dashboard', 'workload-by-professional'];

/**
 * Hook para buscar carga de trabalho futura por profissional
 * 
 * Configurações do React Query:
 * - staleTime: 15 minutos (dados considerados frescos)
 * - gcTime: 10 minutos (tempo de cache após desmontagem)
 * - retry: 3 tentativas com backoff exponencial
 * 
 * @returns {object} Objeto com dados, estados e funções
 * @property {Array} data - Array de objetos com profissional_id, profissional_nome e total_tarefas
 * @property {boolean} isLoading - Indica se está carregando
 * @property {boolean} isError - Indica se houve erro
 * @property {string|null} error - Mensagem de erro (null se não houver erro)
 * @property {function} refetch - Função para recarregar os dados manualmente
 * 
 * @example
 * ```javascript
 * const { data, isLoading, error } = useWorkloadByProfessional();
 * 
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error} />;
 * return <BarChart data={data} />;
 * ```
 */
export const useWorkloadByProfessional = () => {
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: WORKLOAD_BY_PROFESSIONAL_QUERY_KEY,
        queryFn: async () => {
            try {
                const response = await getWorkloadByProfessionalApi();
                return response.data?.data || [];
            } catch (err) {
                logHttpError(err, {
                    component: 'useWorkloadByProfessional',
                    endpoint: '/dashboard/workload-by-professional',
                });
                throw err;
            }
        },
        staleTime: 1000 * 60 * 15, // 15 minutos - dados considerados frescos
        gcTime: 1000 * 60 * 10, // 10 minutos - tempo de cache
        refetchOnWindowFocus: false, // Não refetch ao focar na janela
        refetchOnMount: false, // Não refetch ao montar - usa cache se dados estão frescos
        refetchOnReconnect: true, // Refetch ao reconectar
        retry: (failureCount, error) => {
            if (error?.response?.status === 401 || 
                error?.response?.status === 403 || 
                error?.response?.status === 404) {
                return false;
            }
            return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
        throwOnError: false, // Não lança erro, apenas retorna no objeto error
    });

    // Manter compatibilidade: retornar error como string (se houver)
    const errorMessage = error
        ? getErrorMessage(error, {
            context: 'dashboard',
            action: 'workload',
        })
        : null;

    return {
        data: data || [],
        isLoading,
        isError,
        error: errorMessage,
        refetch,
    };
};
